/**
 * TinyMCE inline editors schedule a throttled selection sync (setTimeout 0) on
 * document mouseup / selection changes. When React unmounts the editor in the
 * same turn (calendar delete, remount, toolbar swap), that callback can run
 * after getBody() is gone and throw "Node cannot be null or undefined".
 *
 * Those throws never hit React error boundaries. Detect them for Sentry
 * beforeSend and for a short-lived window error suppressor while editors live.
 */

function frameLooksLikeTinyMce(frame) {
  const file = String(frame?.filename || frame?.abs_path || "");
  return file.includes("/tinymce/") || file.includes("tinymce.min.js");
}

export function isTinyMceDetachedDomErrorMessage(value) {
  const message = String(value || "");
  return (
    message.includes("Node cannot be null or undefined")
    || message.includes("Cannot read properties of null (reading 'getNode')")
    || (
      message.includes("null is not an object (evaluating")
      && message.includes("getNode")
    )
  );
}

export function isTinyMceDetachedDomErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  if (!isTinyMceDetachedDomErrorMessage(exception.value)) {
    return false;
  }
  const frames = exception.stacktrace?.frames || [];
  return frames.some(frameLooksLikeTinyMce);
}

export function isTinyMceDetachedDomWindowError(event) {
  if (!event) {
    return false;
  }
  const message = event.message || event.error?.message || "";
  if (!isTinyMceDetachedDomErrorMessage(message)) {
    return false;
  }
  const filename = String(event.filename || "");
  if (filename.includes("/tinymce/") || filename.includes("tinymce.min.js")) {
    return true;
  }
  const stack = String(event.error?.stack || "");
  return stack.includes("/tinymce/") || stack.includes("tinymce.min.js");
}

let suppressionRetainCount = 0;
let windowErrorHandler = null;

function handleWindowError(event) {
  if (!isTinyMceDetachedDomWindowError(event)) {
    return;
  }
  event.preventDefault();
  if (typeof event.stopImmediatePropagation === "function") {
    event.stopImmediatePropagation();
  }
}

/**
 * Ref-counted: while any TinyEditor is mounted, swallow the known TinyMCE
 * detached-DOM window errors so they do not surface as Uncaught in the console.
 */
export function retainTinymceDetachedDomErrorSuppression() {
  if (typeof window === "undefined") {
    return () => {};
  }
  if (suppressionRetainCount === 0) {
    windowErrorHandler = handleWindowError;
    window.addEventListener("error", windowErrorHandler);
  }
  suppressionRetainCount += 1;

  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    suppressionRetainCount = Math.max(0, suppressionRetainCount - 1);
    if (suppressionRetainCount === 0 && windowErrorHandler) {
      window.removeEventListener("error", windowErrorHandler);
      windowErrorHandler = null;
    }
  };
}
